import {
  EmployeeNominee,
  EmployementStatus,
  EmployementType,
} from "./Employee";

export interface VestingTemplate {
  id: string;
  vestingTemplateName: string;
  vestingType: VestingType;
  cliffPeriod: number;
  numberOfGrantsAffected: number;
  isEditable: boolean;
  isDefault: boolean;
  schedules: Schedule[];
  includeTimeVesting: boolean;
  includeEventVesting: boolean;
  isFractional: boolean;
  createdAt?: string;
}

export enum VestingType {
  STANDARD = "STANDARD",
  CUSTOM = "CUSTOM",
  MANUAL = "MANUAL",
}

export enum TriggerType {
  BOTH = "BOTH",
  TIME = "TIME",
  EVENT = "EVENT",
}

export type DematAccount = {
  id?: string;
  dematAccountId: string;
  status: "active" | "inactive";
  type: "NSDL" | "CDSL" | "";
  isUserUploaded: boolean | null;
  file?: string;
  fileName?: string;
  fileType?: string;
  s3Path?: string;
  visibleToAdmin?: boolean;
  cmlDocumentId?: string;
};

export interface AddVestingTemplateReq {
  id: string;
  vestingTemplateName: string;
  vestingType: VestingType;
  cliffPeriod: number;
  timeScheduleDtos: Schedule[];
  eventScheduleDtos: Schedule[];
  percentage?: number;
  includeTimeVesting: boolean;
  includeEventVesting: boolean;
  isDefault: boolean;
}
export interface VestingEventReq {
  eventName: string;
  eventTargetDate: string;
  vestingScheduleState: string;
  isEditable: boolean;
  vestingTemplateName: string;
  employeeNames: string[];
  grantsINumber: number;
}

export interface VestingTemplateDetail {
  id: string;
  vestingTemplateName: any;
  isAlreadyInUse: boolean;
  vestingType: VestingType;
  vestingTriggerType: TriggerType;
  cliffPeriod: number;
  vestingInterval: any;
  vestingPeriod: any;
  isDefault: boolean;
  timeScheduleDtos: Schedule[];
  eventScheduleDtos: Schedule[];
}

export interface AddVestingTemplateRes {
  id: string;
  cliff: number;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  companyId: string;
  isDeleted: boolean;
  name: string;
  type: string;
}

export interface Schedule {
  id: string;
  sequenceNumber: number;
  percentage: number;
  vestingDuration: number;
  vestingInterval: number;
  eventName: string;
  eventTargetDate: string;
  eventCompletionDate?: string;
  vestingTriggerType: TriggerType;
  vestingScheduleState: string;
  milestoneId?: string;
  newlyAdded?: boolean;
  targetDateType?: string;
  vestingOffset?: number;
  offsetType?: string;
}

export interface EmployeeVestingDetail {
  date: string;
  vestedOptions: number;
  accumulatedGrantedOptionsForEmployee: number;
  accumulatedVestedOptionsForEmployee: number;
  accumulatedExercisedOptionsForEmployee: number;
  grantedOptions: number;
  exercisedOptions: number;
  exercisedOptionsWithCash: number;
  isFractional: boolean;
  optionHolderState?: string;
  optionHolderId?: string;
  identifier?: string;
  stockExercised?: number;
}

export interface EmployeeGrantTransactionDetail {
  date: string; // actual transaction date
  projectedDate?: string; // original scheduled vest date (VESTED / LAPSED only)
  options: number;
  transactionType: string;
  // e.g. "VESTED" | "ACCELERATION" | "LAPSED" | "CASHED_OUT" | "STOCK_EXERCISE"
  //      "GRANTED" | "SURRENDERED" | "YET TO VEST"
  triggerType?: TriggerType; // "TIME" | "EVENT" | "BOTH"
  currentVested: number;
  netVested: number;
  netStockExercised: number;
  netCashedOut: number;
  totalGranted: number;
  totalTransactedOptions: number;
  identifier?: string; // grant display name, e.g. "G3"
  grantDbId?: string; // actual grant DB UUID, used for API calls
  isFractional?: boolean;
  milestoneName?: string; // e.g. "G3 MILESTONE: ALPHA RELEASE"
  eventName?: string; // buyback/exercise event name for CASHED_OUT/STOCK_EXERCISE
  acceleratedFromDate?: string; // original date the acceleration was triggered from
  repricedStrikePrice?: number;
  strikePrice?: number;
  expiryDate?: string | null;
}
export interface EmployeePageDetails {
  id: string;
  updatedAt: string;
  employeeName: string;
  imageURL: string;
  grantedOptions: number;
  vestedOptions: number;
  exercisedOptions: number;
  sharesReceived: number;
  status: EmployementStatus;
  residentStatus: string;
  department: string;
  emailAddress: string;
  nationality: string;
  designation: string;
  contactNumber: string;
  isLoginProvided: boolean;
  showPPSToEmployees: boolean;
  ppsforEmployee: number;
  dateOfJoin: string;
  managers: string[];
  legalName?: string;
  subsidiaryCompanyId?: string;
  subsidiaryCompanyName?: string;
  salary?: number;
  pan: string;
  address: string;
  grade: string;
  employeeIdentification: string;
  employerName: string;
  employmentType: EmployementType;
  lastDay: string;
  resignationDate?: string;
  notes: string;
  deactivate: boolean;
  isPausedVesting: boolean;
  totalOptionValue: number;
  totalOptionCost: number;
  isFractional: boolean;
  userHasLimitedAccessForEmployee: boolean;
  personalEmail?: string;
  dematAccounts?: DematAccount[];
  nominees: EmployeeNominee[];
  lastCredentialsGeneratedDate: string;
  isResignationSchedule?: boolean;
  additionalData: AdditionalMetaData;
  showOptionsExpiryDateToEmployees: boolean;
  showActiveGrantedOptionsToAdmin: boolean;
  activeGrantedOptions: number;
  activeOptionsValue: number;
  activeOptionsCost: number;
  netVestedOptions: number;
}
export interface AdditionalMetaData {
  credentialsData: string[];
  transferAccessData: string[];
  comments?: string[];
  tags?: { label: string; value: string }[];
  udfValues?: Record<string, any>;
}

export interface EmployeeStockTransactionDetails {
  id: string;
  noOfSoldStock: number;
  unrealisedStock?: number;
  pricePerShare: number;
  amountReceived: number;
  transactionType: string;
  employeeId: string;
  transactionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeStockTransaction {
  amountReceived: number;
  employeeId: string;
  noOfSoldShares: number;
}
export interface Integration {
  secretARN: string;
  token: string;
  tenentname: string;
  status: string;
}

export interface PauseVestingDto {
  employeeId?: string;
  isPausedVesting: boolean;
  esopPlanId?: string;
  optionHolderId?: string;
}

export interface VestingRow {
  grantId: string;
  projectedVestingDate: string;
  projectedVestedOptions: number;
  actualVestedOptions: number;
}

export interface CumulatedVesting extends VestingRow {
  cumulativeProjectedVestedOptions: number;
  cumulativeActualVestedOptions: number;
}
